// src/views/whatsappInbox/components/MessageMedia/index.tsx

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { IoIosCloudDownload } from "react-icons/io";
import { Spin, Modal } from 'antd';
import { FileText, Video, Image as ImageIcon, X, PlayIcon } from 'lucide-react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { 
  downloadMedia, 
  openMediaPreview,
  selectIsMediaDownloading,
  selectMediaError,
  selectMediaCacheItem
} from '../../../store/slices/mediaSlice';
import logger, { Logger } from '../../../utils/logger';
import toast from 'react-hot-toast';

// Styled Components
const MediaWrapper = styled.div<{ thumbnailMode?: boolean }>`
  max-width: ${props => props.thumbnailMode ? '100%' : '350px'};
  width: ${props => props.thumbnailMode ? '100%' : 'auto'};
  height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  border-radius: 8px;
  overflow: hidden;
  background: #f8fafc;
  margin: ${props => props.thumbnailMode ? '0' : '4px 0'};
  
  ${props => props.thumbnailMode && `
    display: flex;
    flex-direction: column;
  `}
`;


const EnhancedAudioPlayer = styled.div`
  width: 100%;
  background-color: #f0f2f5;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  .message.outgoing & {
    background-color: #dcf8c6;
  }
`;

const WaveformVisualization = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  gap: 2px;
  margin-bottom: 4px;
`;

const WaveformBar = styled.div<{ isPlayed: boolean }>`
  flex: 1;
  border-radius: 1px;
  background-color: ${props => props.isPlayed ? '#0084ff' : '#e1e1e1'};
  min-width: 2px;

  .message.outgoing & {
    background-color: ${props => props.isPlayed ? '#25d366' : '#e1e1e1'};
  }
`;

const AudioProgress = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  margin-bottom: 4px;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  background: #0084ff;
  position: absolute;
  left: 0;
  top: 0;
  width: ${props => props.progress}%;
  transition: width 0.1s;

  .message.outgoing & {
    background: #25d366;
  }
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #8696a0;
  margin-bottom: 8px;
`;

const AudioControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: none;
  color: #54656f;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f5f5f5;
  }
`;

const PlayButton = styled(ControlButton)`
  background: #0084ff;
  color: white;
  
  .message.outgoing & {
    background: #25d366;
  }
  
  &:hover {
    background: #0077e6;
    
    .message.outgoing & {
      background: #22c35e;
    }
  }
`;

const SpeedIndicator = styled.span`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
`;

const AudioWrapper = styled.div`
  padding: 8px 12px;
  background: #f0f2f5;
  border-radius: 8px;
  width: 350px; // Increased from 280px to match the screenshot
  max-width: 350px;

  audio {
    width: 100%;
    height: 40px;
  }

  audio::-webkit-media-controls-panel {
    background-color: #ffffff;
  }

  audio::-webkit-media-controls-play-button {
    background-color: #dcf8c6;
    border-radius: 50%;
  }

  audio::-webkit-media-controls-timeline {
    background-color: #f0f2f5;
    border-radius: 2px;
    height: 3px;
  }
`;

const MediaPlaceholder = styled.div<{ thumbnailMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  padding: ${props => props.thumbnailMode ? '12px' : '24px'};
  gap: ${props => props.thumbnailMode ? '8px' : '12px'};
  min-height: ${props => props.thumbnailMode ? '100%' : '120px'};
  cursor: pointer;
  
  &:hover {
    background: #e5e7eb;
  }

  .icon-wrapper {
    background: white;
    padding: ${props => props.thumbnailMode ? '8px' : '12px'};
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .text {
    font-size: 13px;
    color: #64748b;
    text-align: center;
  }
`;

const MediaContent = styled.div<{ thumbnailMode?: boolean }>`
  position: relative;
  cursor: pointer;
  height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  width: ${props => props.thumbnailMode ? '100%' : 'auto'};
  
  ${props => props.thumbnailMode && `
    display: flex;
    flex-direction: column;
  `}
`;

const ImagePreview = styled.img<{ thumbnailMode?: boolean }>`
  width: 100%;
  height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  display: block;
  max-height: ${props => props.thumbnailMode ? 'none' : '300px'};
  min-height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  object-fit: ${props => props.thumbnailMode ? 'cover' : 'contain'};
  border-radius: 8px;
`;

// VIDEO COMPONENT IMPROVEMENTS
// Update the VideoPreview component
const VideoPreview = styled.video<{ thumbnailMode?: boolean }>`
  width: 100%;
  height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  max-height: ${props => props.thumbnailMode ? 'none' : '300px'};
  min-height: ${props => props.thumbnailMode ? '100%' : 'auto'};
  max-width: 350px; // Increased to match other media components
  display: block;
  background: #000;
  object-fit: ${props => props.thumbnailMode ? 'cover' : 'contain'};
  border-radius: 8px; // Added for better appearance
`;

// Add this component for better play button overlay
const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  
  .play-indicator {
    width: 50px;
    height: 50px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; 
  align-items: center;
  justify-content: center;
  color: white;
`;

const DocumentPreview = styled.div`
  padding: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 350px; // Set explicit max width
  
  .doc-icon {
    color: #e94235;
    min-width: 40px;
    display: flex;
    justify-content: center;
  }
  
  .doc-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    
    .filename {
      font-weight: 500;
      color: #1f1f1f;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .filetype {
      font-size: 12px;
      color: #64748b;
    }
  }
  
  .doc-download {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 50%;
    color: #3b82f6;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 36px;
    height: 36px;
    
    &:hover {
      background: #f1f5f9;
      color: #2563eb;
      transform: scale(1.1);
    }
    
    &:active {
      transform: scale(0.95);
    }
  }
`;

const DownloadButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.05);
  color: #666;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const PreviewModal = styled(Modal)`
  && {
    .ant-modal-content {
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      border-radius: 0 !important;
      max-width: 95vw !important;
      max-height: 95vh !important;
    }
    
    .ant-modal-body {
      padding: 0 !important;
      background: transparent !important;
    }
    
    .ant-modal-close {
      position: fixed !important;
      top: 24px !important;
      right: 24px !important;
      color: white !important;
      background: rgba(0, 0, 0, 0.8) !important;
      border-radius: 50% !important;
      padding: 12px !important;
      z-index: 1001 !important;
      width: 48px !important;
      height: 48px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      backdrop-filter: blur(8px) !important;
      border: 2px solid rgba(255, 255, 255, 0.1) !important;
      transition: all 0.3s ease !important;

      &:hover {
        background: rgba(255, 255, 255, 0.2) !important;
        transform: scale(1.1) !important;
        border-color: rgba(255, 255, 255, 0.3) !important;
      }

      .ant-modal-close-x {
        font-size: 20px !important;
        line-height: 1 !important;
      }
    }

    .preview-content {
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      min-height: 90vh !important;
      position: relative !important;
      background: rgba(0, 0, 0, 0.95) !important;
      backdrop-filter: blur(20px) !important;
      
      &.document {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        padding: 40px !important;
        border-radius: 20px !important;
        max-height: 90vh !important;
        overflow-y: auto !important;
        margin: 20px !important;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
      }
      
      img, video {
        max-height: 90vh !important;
        max-width: 90vw !important;
        object-fit: contain !important;
        border-radius: 12px !important;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5) !important;
      }
    }
  }
  
  /* Custom scrollbar for document preview */
  .preview-content.document {
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.3);
      border-radius: 4px;
      
      &:hover {
        background: rgba(255, 255, 255, 0.5);
      }
    }
  }
`;

// Modern Preview Action Bar
const PreviewActionBar = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(16px);
  border-radius: 50px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 1001;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    bottom: 20px;
    padding: 10px 20px;
    gap: 12px;
  }
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  background: ${props => props.primary ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  border: 2px solid ${props => props.primary ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.primary ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
    transform: scale(1.1);
    border-color: rgba(255, 255, 255, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`;

const MediaInfo = styled.div`
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(16px);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  border: 2px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 12px;
  }
`;

const ProgressIndicator = styled.div<{ progress: number, thumbnailMode?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: ${props => props.thumbnailMode ? '2px' : '3px'};
  background: #3b82f6;
  width: ${props => props.progress}%;
  transition: width 0.2s;
  z-index: 5;
`;

const PDFPreviewContainer = styled.div`
  .preview-content.document {
    background: white;
    border-radius: 8px;
    padding: 24px;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .pdf-iframe-container {
    position: relative;
    width: 100%;
    height: 80vh;
  }
  
  .pdf-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 40px 20px;
    text-align: center;
    
    h3 {
      margin: 0;
      color: #1f1f1f;
      font-size: 18px;
    }
    
    p {
      margin: 0;
      color: #64748b;
      font-size: 14px;
    }
    
    .fallback-buttons {
      display: flex;
      gap: 12px;
      margin-top: 16px;
    }
    
    .btn-primary, .btn-secondary {
      padding: 12px 24px;
      border-radius: 8px;
      font-weight: 500;
      text-decoration: none;
      cursor: pointer;
      border: none;
      font-size: 14px;
      transition: all 0.2s;
    }
    
    .btn-primary {
      background: #3b82f6;
      color: white;
      
      &:hover {
        background: #2563eb;
      }
    }
    
    .btn-secondary {
      background: #f1f5f9;
      color: #475569;
      
      &:hover {
        background: #e2e8f0;
      }
    }
  }
  
  .pdf-error {
    color: #ef4444;
    font-size: 14px;
    margin-top: 8px;
  }
`;

// PDF Preview Wrapper Component
interface PDFPreviewWrapperProps {
  url: string;
  filename?: string;
}

const PDFPreviewWrapper: React.FC<PDFPreviewWrapperProps> = ({ url, filename }) => {
  const [iframeError, setIframeError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIframeError(true);
    setIsLoading(false);
    Logger.log("PDF iframe failed to load:", url);
  };

  const openInNewTab = () => {
    window.open(url, '_blank');
  };

  const downloadFile = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (iframeError) {
    return (
      <PDFPreviewContainer>
        <div className="preview-content document">
          <div className="pdf-fallback">
            <FileText size={48} color="#3b82f6" />
            <h3>{filename || 'PDF Document'}</h3>
            <p>Unable to preview this PDF in browser</p>
            <div className="fallback-buttons">
              <button className="btn-primary" onClick={openInNewTab}>
                Open in New Tab
              </button>
              <button className="btn-secondary" onClick={downloadFile}>
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </PDFPreviewContainer>
    );
  }

  return (
    <PDFPreviewContainer>
      <div className="preview-content document">
        {isLoading && (
          <div style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 10 
          }}>
            <Spin size="large" />
          </div>
        )}
        <div className="pdf-iframe-container">
          <iframe
            src={url}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: isLoading ? 'none' : 'block'
            }}
            title="PDF Preview"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
        <PreviewActionBar>
          <ActionButton 
            onClick={downloadFile}
            title="Download PDF"
            primary
          >
            <IoIosCloudDownload size={20} />
          </ActionButton>
          
          <ActionButton 
            onClick={openInNewTab}
            title="Open in new tab"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </ActionButton>
          
          <MediaInfo>
            {filename || 'PDF Document'}
          </MediaInfo>
        </PreviewActionBar>
      </div>
    </PDFPreviewContainer>
  );
};

// Reusable Audio Player Component
interface AudioPlayerProps {
  url: string;
  filename?: string;
  isPreviewMode?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ url, filename, isPreviewMode = false }) => {
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedIndicator, setSpeedIndicator] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const [barHeights] = useState(() =>
    Array.from({ length: 30 }, () => 10 + Math.random() * 25)
  );

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setAudioProgress(progress);
      setAudioCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleDurationChange = useCallback(() => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const position = (e.clientX - rect.left) / progressBar.clientWidth;
      audioRef.current.currentTime = position * audioRef.current.duration;
    }
  }, []);

  return (
    <AudioWrapper style={isPreviewMode ? { maxWidth: '600px', margin: '0 auto' } : {}}>
      <EnhancedAudioPlayer>
        <audio
          ref={audioRef}
          src={url}
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          hidden
        />

        <WaveformVisualization
          onClick={handleProgressClick}
          style={{ cursor: 'pointer' }}
          title="Click to seek"
        >
          {barHeights.map((height, index) => {
            const isPlayedBar = audioProgress > (index / barHeights.length) * 100;
            return (
              <WaveformBar
                key={index}
                style={{ height: `${height}px` }}
                isPlayed={isPlayedBar}
              />
            );
          })}
        </WaveformVisualization>

        <AudioProgress onClick={handleProgressClick}>
          <ProgressBar progress={audioProgress} />
        </AudioProgress>

        <TimeDisplay>
          <span>{formatTime(audioCurrentTime)}</span>
          <span>{formatTime(audioDuration || 0)}</span>
        </TimeDisplay>

        <AudioControls>
          <PlayButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (audioRef.current) {
                if (isPlaying) {
                  audioRef.current.pause();
                } else {
                  audioRef.current.play().catch(err => {
                    logger.log('Audio play error:', err);
                    toast.error('Failed to play audio');
                  });
                }
              }
            }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            )}
          </PlayButton>

          <ControlButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (audioRef.current) {
                const speeds = [1, 1.5, 2];
                const currentSpeed = audioRef.current.playbackRate;
                const currentIndex = speeds.indexOf(currentSpeed);
                const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
                audioRef.current.playbackRate = nextSpeed;
                setSpeedIndicator(`${nextSpeed}x`);
                setTimeout(() => setSpeedIndicator(''), 1000);
              }
            }}
            title="Playback speed"
            style={{ position: 'relative', fontSize: '11px', fontWeight: '600' }}
          >
            {speedIndicator || '1x'}
          </ControlButton>

          <ControlButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (url) {
                const a = document.createElement('a');
                a.href = url;
                a.download = filename || 'audio';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                toast.success('Audio download started');
              }
            }}
            title="Download audio"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </ControlButton>
        </AudioControls>
      </EnhancedAudioPlayer>
    </AudioWrapper>
  );
};

// Types
interface MediaMessage {
  _id: string;
  messageType: 'video' | 'image' | 'document' | 'audio'; // Added audio
  downloadUrl?: string;
  message: {
    body?: string;
    id?: string;
    mime_type?: string;
    filename?: string;
    voice?: boolean; // Added voice property
  };
}

interface MessageMediaProps {
  message: MediaMessage;
  onDownload: () => Promise<string | null>; // Changed to return URL
  onPreview?: () => void;
  integrationInfo: any;
  thumbnailMode?: boolean;
  isInGroup?: boolean;
}

export const MessageMedia: React.FC<MessageMediaProps> = ({
  message,
  onDownload,
  onPreview,
  integrationInfo,
  thumbnailMode = false,
  isInGroup = false
}) => {

  const dispatch = useAppDispatch();

  // Video playing state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Use Redux selectors for loading and error states
  const isLoading = useAppSelector(selectIsMediaDownloading(message._id));
  const loadError = useAppSelector(selectMediaError(message._id));
  
  // Get cached media URL from Redux store
  const cachedMediaItem = useAppSelector(selectMediaCacheItem(message._id));
  const cachedMediaUrl = cachedMediaItem?.url;
  
  // Keep local state for UI components
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // Cache lookup helper - now properly uses Redux cache
  const getCachedUrl = useCallback(() => {
    const url = cachedMediaUrl || message.downloadUrl;
   
    return url;
  }, [cachedMediaUrl, message.downloadUrl, message._id, cachedMediaItem]);


  const handleMediaClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = getCachedUrl();
    if (url) {
      logger.log("MessageMedia - Using cached URL, opening preview directly");
      handlePreview();
      return;
    }
    
    try {
      setDownloadProgress(0);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Use provided onDownload handler
      await onDownload();
      
      clearInterval(progressInterval);
      setDownloadProgress(100);

      // After successful download, show preview
      handlePreview();
    } catch (error) {
      Logger.log('Media download failed:', error);
    }
  };


  const handlePreview = () => {
    // Use Redux preview handler if provided
    if (onPreview) {
      Logger.log("MessageMedia - Calling onPreview handler for:", message._id);
      onPreview();
    } else if (message.downloadUrl || getCachedUrl()) {
      // Otherwise use local modal state
      Logger.log("MessageMedia - Opening local preview with URL available");
      setIsPreviewOpen(true);
    } else {
      Logger.log("MessageMedia - Error: Tried to preview media without URL");
      // Handle the error - maybe show an error message or retry download
      toast.error("Media not available for preview");
    }
  };

  const renderMediaContent = () => {
    const url = getCachedUrl();
    if (url) {
      logger.log("MessageMedia - Rendering with cached URL:", url ? "Available" : "Not available");
      
      switch (message.messageType) {
        case 'image':
          return (
            <div className="media-preview" style={{ height: thumbnailMode ? '100%' : 'auto' }}>
              <ImagePreview
                src={url}
                alt={message.message.filename || 'Image'}
                loading="lazy"
                onClick={handlePreview}
                thumbnailMode={thumbnailMode}
              />
              {thumbnailMode && (
                <div className="preview-overlay" onClick={handlePreview}>
                  <div className="play-indicator"></div>
                </div>
              )}
            </div>
          );
          
          case 'video':
            return (
              <div className="media-preview" style={{ position: 'relative', height: thumbnailMode ? '100%' : 'auto' }}>
                <VideoPreview
                  controls={!thumbnailMode}
                  preload="metadata"
                  thumbnailMode={thumbnailMode}
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onEnded={() => setIsVideoPlaying(false)}
                >
                  <source src={url} type={message.message.mime_type} />
                </VideoPreview>

                {!isVideoPlaying && (
                  <VideoOverlay onClick={handlePreview}>
                    <div className="play-indicator">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    </div>
                  </VideoOverlay>
                )}
              </div>
            );

        case 'audio':
          return <AudioPlayer url={url} filename={message.message.filename} />;
        
        case 'document':
          return (
            <DocumentPreview onClick={handlePreview}>
              <div className="doc-icon">
                <FileText size={24} />
              </div>
              <div className="doc-info">
                <span className="filename">
                  {message.message.filename || 'Document'}
                </span>
                <span className="filetype">
                  {message.message.mime_type?.split('/')[1]?.toUpperCase() || 'FILE'}
                </span>
              </div>
              <div className="doc-download" onClick={(e) => {
                e.stopPropagation();
                if (url) {
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = message.message.filename || 'document';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}>
                <IoIosCloudDownload size={20} />
              </div>
            </DocumentPreview>
          );
      }
    }
  
    // Better placeholder with type-specific icons
    return (
      <MediaPlaceholder onClick={handleMediaClick} thumbnailMode={thumbnailMode}>
        <div className="icon-wrapper">
          {message.messageType === 'image' && <ImageIcon size={thumbnailMode ? 18 : 24} color="#3b82f6" />}
          {message.messageType === 'video' && <Video size={thumbnailMode ? 18 : 24} color="#3b82f6" />}
          {message.messageType === 'document' && <FileText size={thumbnailMode ? 18 : 24} color="#3b82f6" />}
          {message.messageType === 'audio' && (
            <svg width={thumbnailMode ? "18" : "24"} height={thumbnailMode ? "18" : "24"} viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" x2="12" y1="19" y2="22"/>
            </svg>
          )}
        </div>
        {!thumbnailMode && (
          <div className="text">
            Click to download {message.messageType}
          </div>
        )}
      </MediaPlaceholder>
    );
  };

  const handleDownloadMedia = () => {
    const url = getCachedUrl() || message.downloadUrl;
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = message.message.filename || `${message.messageType}-${Date.now()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNewTab = () => {
    const url = getCachedUrl() || message.downloadUrl;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const renderPreviewContent = () => {
    const url = getCachedUrl() || message.downloadUrl;
    if (!url) return null;
  
    const actionBar = (
      <PreviewActionBar>
        <ActionButton 
          onClick={handleDownloadMedia}
          title="Download"
        >
          <IoIosCloudDownload size={20} />
        </ActionButton>
        
        <ActionButton 
          onClick={handleOpenInNewTab}
          title="Open in new tab"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </ActionButton>
        
        <MediaInfo>
          {message.message.filename || `${message.messageType?.toUpperCase()} File`}
        </MediaInfo>
      </PreviewActionBar>
    );
  
    switch (message.messageType) {
      case 'image':
        return (
          <>
            <div className="preview-content">
              <img 
                src={url}
                alt={message.message.filename || 'Image'}
              />
            </div>
            {actionBar}
          </>
        );
  
      case 'video':
        return (
          <>
            <div className="preview-content">
              <video
                src={url}
                controls
                autoPlay
              />
            </div>
            {actionBar}
          </>
        );
      
      case 'audio':
        return (
          <>
            <div className="preview-content" style={{ padding: '40px' }}>
              <AudioPlayer url={url} filename={message.message.filename} isPreviewMode={true} />
            </div>
            {actionBar}
          </>
        );
  
      case 'document':
        if (message.message.mime_type?.includes('pdf')) {
          return <PDFPreviewWrapper url={url} filename={message.message.filename} />;
        }
        return (
          <>
            <div className="preview-content document">
              <div className="download-prompt">
                <FileText size={48} className="mb-4" />
                <h3>{message.message.filename}</h3>
                <a 
                  href={url}
                  download
                  className="download-button"
                  onClick={(e) => e.stopPropagation()}
                >
                  Download File
                </a>
              </div>
            </div>
            {actionBar}
          </>
        );
    }
  };

  return (
    <MediaWrapper thumbnailMode={thumbnailMode}>
      <MediaContent thumbnailMode={thumbnailMode}>
        {renderMediaContent()}
        
        {isLoading && (
          <LoadingOverlay>
            <Spin size={thumbnailMode ? "small" : "default"} />
          </LoadingOverlay>
        )}
  
        {loadError && (
          <LoadingOverlay>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ff4444', marginBottom: thumbnailMode ? '4px' : '8px', fontSize: thumbnailMode ? '11px' : '14px' }}>
                Error
              </div>
              {!thumbnailMode && (
                <button 
                  onClick={handleMediaClick}
                  style={{
                    padding: '6px 12px',
                    background: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          </LoadingOverlay>
        )}
  
        {downloadProgress > 0 && downloadProgress < 100 && (
          <ProgressIndicator progress={downloadProgress} thumbnailMode={thumbnailMode} />
        )}
      </MediaContent>
  
      {/* Only render the local modal if not using Redux preview */}
      {!onPreview && (
        <PreviewModal
          open={isPreviewOpen}
          onCancel={() => setIsPreviewOpen(false)}
          footer={null}
          width="auto"
          centered
          closeIcon={<X className="text-white" />}
        >
          {renderPreviewContent()}
        </PreviewModal>
      )}
    </MediaWrapper>
  );
};

export default MessageMedia;